import { AgentFollowSignal, AgentRunStatus, AgentScope, AgentTarget, isLotId, isObjectId, normalizeTarget } from './agent-ia.models';
import type { AgentTimers } from './agent-ia.state';

// Follow mode (back/docs/agent-v2/SPEC.md §8 `target`, follow mode): « à chaque fois que l'IA touche quelque chose on
// redirige le user vers la page ». Pure engine, no Angular import (tested with node:test); AgentFollowService plugs the
// router, the document and the timers in.
// - every fresh event (seq > followFloor of the panel state) arrives once through signal(); data changes are always
//   published (changes$), whatever the follow state;
// - a navigation intent is only executed for a run that was active when the event was written (still valid 10 s after
//   the run ends), in the tab that started the run, follow not paused, tab visible, current page whitelisted (registered
//   page or read-only route), no modal, no blockReason() from a registered page, no user activity for 3 s, at most one
//   follow navigation per 2.5 s and 8 s on a page before switching between the DPGF and a plan; announced 1.5 s before;
//   the latest intent wins. Otherwise it stays pending and the bubble offers « Aller voir » / « Enregistrer et y aller ».
// - manual navigation pauses following: NavigationStart without AGENT_FOLLOW_INFO (popstate always manual, same path
//   ignored, panel links ignored, navigations of a follow chain ignored), or userTookControl(). Each new run resumes it.
// - the DPGF activity feed (agent-follow-feed.ts: Claude, colleagues) publishes through feedChange(): changes$ and
//   highlights only, never a navigation; a change seen by both the panel and the feed is published once.

export const AGENT_FOLLOW_INFO: { readonly agentFollow: true } = Object.freeze({ agentFollow: true as const });
// Links of the agent panel: never pause following.
export const AGENT_PANEL_INFO: { readonly agentPanel: true } = Object.freeze({ agentPanel: true as const });

export const FOLLOW = Object.freeze({
  dwellMs: 2500,        // at most one follow navigation every 2.5 s (from the end of the previous one)
  pageTypeMs: 8000,     // time on a page before switching between the DPGF and a plan
  idleMs: 3000,         // no pointer / key / wheel activity in the workspace for 3 s
  announceMs: 1500,     // « Ouverture du lot … » with « Rester ici » before navigating
  graceMs: 10000,       // an intent of the run stays valid 10 s after the run ends
  recheckMs: 1000,      // blocked intent (modal, guard, other page) checked again every second
  chainMs: 5000,        // a navigation without user input right after a follow navigation is a redirect of it
  highlightMs: 120000,  // posts to highlight wait 2 min for the page that shows their lot
  highlightIds: 200,
  flushMs: 20000        // a page that has not saved after 20 s has failed
});

export interface AgentDataChange {
  seq: number; runId: string; changeSetId: string;
  kind: 'dpgf' | 'plan' | 'project' | 'library'; action?: string; undone: boolean;
  dpgfId?: string;               // target.dpgfId, otherwise conversation.scope.dpgfId
  lotIds: string[] | null;       // null = unknown → every lot may have changed
  removedLotIds: string[]; postIds: string[]; ops: string[];
  planId?: string; planIds: string[];
  author?: AgentChangeAuthor;    // change read by the DPGF activity feed (feedChange); absent = the agent of this panel
}
// Who wrote a change seen by the DPGF activity feed (docs/mcp/06-FRONT.md §8.2, R12): Claude or a « Connexion IA » (source
// 'mcp'), or the agent (source 'agent'), of this user (mine) or of a colleague (name).
export interface AgentChangeAuthor { source: 'mcp' | 'agent'; tag: 'Claude' | 'Connexion IA' | 'Agent IA'; name: string; mine: boolean; }
// Changes already published, by `${applied|undone}:${changeSetId}`: the feed never publishes again what the panel has, and
// the panel skips what the feed published first. Kept 10 min, 500 at most.
const DELIVERED_MS = 600000, DELIVERED_MAX = 500;
// Block reasons a page's flush() can clear (« Enregistrer et y aller »); other reasons (drawer, drag, conflict…) only wait.
export const FLUSHABLE_REASONS = Object.freeze({ saving: 'Enregistrement en cours', dirty: 'Modifications non enregistrées', typing: 'Saisie en cours' });
const FLUSHABLE: string[] = Object.values(FLUSHABLE_REASONS);
export type AgentNavKind = 'project' | 'dpgf' | 'plan' | 'plans' | 'library' | 'home';
// label: the place, with its article (« le lot Gros œuvre », « la DPGF », « le projet « Villa » »), for the bubble texts.
export interface AgentNavIntent { kind: AgentNavKind; projectId?: string; dpgfId?: string; lotId?: string; planId?: string; folderId?: string; label: string; }
export interface AgentFollowPage {
  blockReason?(): string | null;           // e.g. 'Modifications non enregistrées', 'Éditeur CCTP ouvert', 'Tracé en cours'
  shows?(intent: AgentNavIntent): boolean;  // true → already displayed: no navigation (the refresh is enough)
  flush?(): Promise<boolean>;               // saves the pending edits; true when saved or nothing to save
}
export interface AgentFollowEnv {
  url(): string;                            // current router URL
  navigate(commands: string[], extras: { info: typeof AGENT_FOLLOW_INFO; queryParams?: Record<string, string> }): Promise<boolean>;
  visible(): boolean;                       // document.visibilityState
  modalOpen(): boolean;                     // Bootstrap modal or PrimeNG mask
  now(): number;
  timers: AgentTimers;
  changed(): void;                          // the bubble view changed
  emit(change: AgentDataChange): void;      // changes$
}
// Why the pending intent waits ('' = it will be executed when its time comes).
export type AgentFollowHold = '' | 'paused' | 'other-tab' | 'expired' | 'hidden' | 'modal' | 'elsewhere' | 'guard';
export interface AgentNavigationStart { id: number; url: string; popstate: boolean; info: unknown; }

const KINDS: AgentDataChange['kind'][] = ['dpgf', 'plan', 'project', 'library'];
const ACTIVE: AgentRunStatus[] = ['queued', 'running'];
const PLAN_QUANTITY_OPS = ['setDerivedQuantity', 'updatePost'];
// Read-only routes where a follow navigation loses nothing (the editors register guards with register()).
const READ_ONLY_PAGES = [
  /^\/architect\/my-consultations$/, /^\/architect\/ce-project\/[a-f\d]{24}(?:\/fiche-ao)?$/i, /^\/architect\/dpgf\/[a-f\d]{24}(?:\/plans(?:\/[a-f\d]{24})?)?$/i
];
const pathOf = (url: string) => (url || '').split(/[?#;]/)[0].replace(/\/+$/, '') || '/';
const str = (value: unknown, max: number) => typeof value === 'string' ? value.slice(0, max) : '';
type PageType = 'home' | 'project' | 'dpgf' | 'plans' | 'plan' | 'library' | 'other';
function pageType(path: string): PageType {
  if (/^\/architect\/my-consultations$/.test(path)) return 'home';
  if (/^\/architect\/ce-project\/[^/]+$/.test(path)) return 'project';
  if (/^\/architect\/dpgf\/[^/]+$/.test(path)) return 'dpgf';
  if (/^\/architect\/dpgf\/[^/]+\/plans$/.test(path)) return 'plans';
  if (/^\/architect\/dpgf\/[^/]+\/plans\/[^/]+$/.test(path)) return 'plan';
  if (/^\/architect\/archi-db$/.test(path)) return 'library';
  return 'other';
}

// Router commands of an intent (always navigated with { info: AGENT_FOLLOW_INFO }).
export function intentRoute(intent: AgentNavIntent): { commands: string[]; queryParams?: Record<string, string>; path: string } {
  const route = (commands: string[], queryParams?: Record<string, string>) => ({ commands, ...(queryParams ? { queryParams } : {}), path: commands.join('/') });
  switch (intent.kind) {
    case 'project': return route(['/architect/ce-project', intent.projectId!]);
    case 'dpgf': return route(['/architect/dpgf', intent.dpgfId!], intent.lotId ? { lotId: intent.lotId } : undefined);
    case 'plan': return route(['/architect/dpgf', intent.dpgfId!, 'plans', intent.planId!]);
    case 'plans': return route(['/architect/dpgf', intent.dpgfId!, 'plans']);
    case 'library': return route(['/architect/archi-db'], intent.folderId ? { idLot: intent.folderId } : undefined);
    default: return route(['/architect/my-consultations']);
  }
}
const deliveredKey = (change: AgentDataChange) => change && typeof change.changeSetId === 'string' && change.changeSetId ? `${change.undone ? 'undone' : 'applied'}:${change.changeSetId}` : '';
const intentKey = (intent: AgentNavIntent | null) => intent ? JSON.stringify([intent.kind, intent.projectId, intent.dpgfId, intent.lotId, intent.planId, intent.folderId]) : '';
// « Ouverture du lot Gros œuvre… », « Ouverture de la DPGF… », « Retour à vos projets… ».
export function announceText(intent: AgentNavIntent): string {
  if (intent.kind === 'home') return 'Retour à vos projets…';
  const place = intent.label.replace(/^le /, 'du ').replace(/^les /, 'des ').replace(/^la /, 'de la ');
  return `Ouverture ${/^(du|des|de la) /.test(place) ? place : `de ${place}`}…`;
}

export class AgentFollower {
  paused = false;                          // « Rester ici », switch off, manual navigation, userTookControl()
  owned = true;                            // the run was started (or adopted) in this tab
  pending: AgentNavIntent | null = null;   // latest intent of the run, not executed yet
  hold: AgentFollowHold = '';
  holdText = '';                           // blockReason() of the current page
  canSave = false;                         // the blocking page can save its edits (« Enregistrer et y aller »)
  announcing = false;                      // 1.5 s announcement before the navigation
  offer: AgentNavIntent | null = null;     // link only (« Voir le plan », « Voir dans la DPGF »)
  message = '';
  working = false;                         // explicit « Aller voir » in progress

  private pages = new Set<AgentFollowPage>();
  private run: { id: string; status: AgentRunStatus; endedAt: number } | null = null;
  private pendingRun = '';                 // run that produced the pending intent
  private forced = false;                  // home after an undone project creation: always executed
  private conversationId = '';
  private lastProject: AgentScope = {};
  private timer: unknown = null;
  private announced = { key: '', at: 0 };
  private lastActivity = -Infinity;        // pointer / key / wheel in the workspace
  private lastInput = -Infinity;           // pointerdown / keydown in the workspace: a manual navigation needs one
  private navigating = false;              // a follow navigation (and its redirects) is in progress
  private lastFollowStart = -Infinity;
  private lastFollowEnd = -Infinity;
  private pageSince = -Infinity;
  private highlights = new Map<string, { ids: string[]; at: number }>();
  private panelChanges = new Map<string, number>();  // changes published from the panel's conversation
  private feedChanges = new Map<string, number>();   // changes published by the DPGF activity feed
  private view = '';
  private disposed = false;

  constructor(private env: AgentFollowEnv) {}

  get following() { return this.owned && !this.paused; }

  // ---- Pages (WP4 consumers) ------------------------------------------------------------

  register(page: AgentFollowPage): () => void {
    this.pages.add(page); this.soon();
    return () => { if (this.pages.delete(page)) this.soon(); };
  }
  // The user chose another lot himself: stop following until « Reprendre le suivi » or the next run.
  userTookControl() { this.pause(); }
  takeHighlight(dpgfId: string, lotId: string): string[] {
    const now = this.env.now(), ids = new Set<string>();
    for (const key of [`${dpgfId}/${lotId}`, `${dpgfId}/*`]) {
      const entry = this.highlights.get(key);
      this.highlights.delete(key);
      if (entry && now - entry.at <= FOLLOW.highlightMs) entry.ids.forEach(id => ids.add(id));
    }
    return [...ids];
  }
  // ---- DPGF activity feed (docs/mcp/06-FRONT.md §8.2: Claude and the colleagues, agent-follow-feed.ts) ----------------

  // A change read by the feed: published on changes$ like the panel's (the pages refresh, or show their banner when the
  // user has unsaved edits), its posts highlighted; never a follow navigation. false: already published (panel or feed).
  feedChange(change: AgentDataChange): boolean {
    if (this.disposed || !change) return false;
    const key = deliveredKey(change);
    if (key && (this.panelChanges.has(key) || this.feedChanges.has(key))) return false;
    if (key) this.remember(this.feedChanges, key);
    if (!change.undone && change.dpgfId && change.postIds.length) this.addHighlight(change.dpgfId, change.lotIds, change.postIds);
    try { this.env.emit(change); } catch { /* a page error never stops the follow engine */ }
    return true;
  }
  // Posts to highlight when their lot is shown (?mcpChange link, « Voir » of the feed card): takeHighlight() of the page.
  highlight(dpgfId: string, lotIds: string[] | null, postIds: string[]) {
    if (!this.disposed && dpgfId && Array.isArray(postIds) && postIds.length) this.addHighlight(dpgfId, lotIds, postIds);
  }
  // What the registered pages would lose if left now (« Enregistrer et voir » of the feed card).
  pageBlock(): { reason: string; canSave: boolean } {
    const reason = this.blockReason();
    return { reason, canSave: Boolean(reason) && FLUSHABLE.includes(reason) && [...this.pages].some(page => typeof page.flush === 'function') };
  }
  private remember(map: Map<string, number>, key: string) {
    const now = this.env.now();
    if (map.size >= DELIVERED_MAX) for (const [old, at] of map) if (now - at > DELIVERED_MS || map.size >= DELIVERED_MAX) map.delete(old);
    map.set(key, now);
  }

  // Panel send / answer / approve / undo, « Enregistrer et y aller »: every registered page saves its pending edits.
  async flushPages(): Promise<boolean> {
    const pages = [...this.pages].filter(page => typeof page.flush === 'function');
    if (!pages.length) return true;
    let timer: unknown = null;
    const timeout = new Promise<boolean>(resolve => { timer = this.env.timers.setTimeout(() => resolve(false), FOLLOW.flushMs); });
    const saved = Promise.all(pages.map(page => Promise.resolve().then(() => page.flush!()).then(result => result === true, () => false))).then(results => results.every(Boolean));
    try { return await Promise.race([saved, timeout]); } finally { this.env.timers.clearTimeout(timer); }
  }

  // ---- Events of the conversation -------------------------------------------------------

  signal(signal: AgentFollowSignal) {
    if (this.disposed) return;
    if (signal.conversationId !== this.conversationId) { this.reset(); this.conversationId = signal.conversationId; }
    const { event } = signal, run = signal.run, now = this.env.now();
    this.owned = signal.owned;
    if (signal.scope.projectId) this.lastProject = { ...signal.scope };
    if (run && run.id !== this.run?.id) {
      // Each new run follows again and drops what the previous one left.
      this.run = { id: run.id, status: run.status, endedAt: ACTIVE.includes(run.status) ? 0 : now };
      this.paused = false; this.message = ''; this.offer = null;
      if (!this.forced) this.drop();
    } else if (run && this.run) {
      if (ACTIVE.includes(this.run.status) && !ACTIVE.includes(run.status)) this.run.endedAt = now;
      if (ACTIVE.includes(run.status)) this.run.endedAt = 0;
      this.run.status = run.status;
    }
    const active = Boolean(run && ACTIVE.includes(run.status) && (!event.runId || event.runId === run.id));
    switch (event.type) {
      case 'changeset_applied': case 'measurement_proposed': case 'changeset_undone': this.dataChange(signal, active); break;
      case 'conversation_scoped': this.scoped(signal, active); break;
      case 'tool_started': this.toolStarted(signal, active); break;
    }
    this.attempt();
  }
  // Conversation left (another one selected, new conversation, panel destroyed).
  reset() {
    this.drop(); this.clearTimer();
    this.run = null; this.conversationId = ''; this.lastProject = {}; this.offer = null; this.message = ''; this.paused = false; this.owned = true;
    this.highlights.clear(); this.update();
  }
  dispose() { this.reset(); this.disposed = true; }

  private dataChange(signal: AgentFollowSignal, active: boolean) {
    const { event, scope } = signal, data = event.data, target = normalizeTarget(data.target), undone = event.type === 'changeset_undone';
    const kind = (event.type === 'measurement_proposed' ? 'plan' : KINDS.find(value => value === data.kind) || KINDS.find(value => value === signal.knownKind)) as AgentDataChange['kind'] | undefined;
    if (!kind) return;
    const action = str(data.action, 30), planId = target.planId || (isObjectId(data.planId) ? data.planId : undefined);
    const dpgfId = target.dpgfId || (kind === 'dpgf' || kind === 'plan' ? (isObjectId(scope.dpgfId) ? scope.dpgfId : undefined) : undefined);
    const change: AgentDataChange = {
      seq: event.seq, runId: event.runId, changeSetId: str(data.changeSetId, 200), kind, ...(action ? { action } : {}), undone, ...(dpgfId ? { dpgfId } : {}),
      lotIds: target.lotIds ? [...target.lotIds] : null, removedLotIds: target.removedLotIds || [], postIds: target.postIds || [], ops: target.ops || [],
      ...(planId ? { planId } : {}), planIds: target.planIds || []
    };
    const key = deliveredKey(change), byFeed = Boolean(key) && this.feedChanges.has(key);
    if (key) this.remember(this.panelChanges, key);
    // Already published by the DPGF activity feed: the pages have it; only the follow intent below is new.
    if (!byFeed) {
      if (!undone && dpgfId && change.postIds.length) this.addHighlight(dpgfId, change.lotIds, change.postIds);
      try { this.env.emit(change); } catch { /* a page error never stops the follow engine */ }
    }
    if (undone) { if (kind === 'project') this.projectRemoved(target.projectId, target.dpgfId); return; }
    if (!active) return;
    const intent = this.intentFor(change, target);
    if (intent === 'offer-dpgf') this.offer = { kind: 'dpgf', dpgfId: change.dpgfId!, ...(change.lotIds?.length === 1 ? { lotId: change.lotIds[0] } : {}), label: 'la DPGF' };
    else if (intent === 'offer-library') this.offer = { kind: 'library', ...(target.folderId ? { folderId: target.folderId } : {}), label: 'la bibliothèque' };
    else if (intent) this.want(intent, event.runId || signal.run!.id);
  }
  private intentFor(change: AgentDataChange, target: AgentTarget): AgentNavIntent | 'offer-dpgf' | 'offer-library' | null {
    const { dpgfId, ops } = change, current = pageType(pathOf(this.env.url()));
    switch (change.kind) {
      case 'dpgf': {
        if (!dpgfId || (ops.length && ops.every(op => op === 'reorderLots'))) return null;
        if (change.removedLotIds.length) return { kind: 'dpgf', dpgfId, label: 'la DPGF' };
        if (current === 'plan' && ops.length && ops.every(op => PLAN_QUANTITY_OPS.includes(op))) return 'offer-dpgf';
        const lotId = change.lotIds?.length === 1 ? change.lotIds[0] : undefined;
        const title = lotId ? target.lots?.find(lot => lot.id === lotId)?.title : '';
        return lotId ? { kind: 'dpgf', dpgfId, lotId, label: title ? `le lot ${title}` : 'le lot modifié' } : { kind: 'dpgf', dpgfId, label: 'la DPGF' };
      }
      case 'plan':
        if (!dpgfId) return null;
        if (change.action === 'attach') return { kind: 'plans', dpgfId, label: 'les plans' };
        return change.planId ? { kind: 'plan', dpgfId, planId: change.planId, label: 'le plan' } : null;
      // The library page does not register as a follower: going there would hold every later intent of the run (« Aller voir »).
      case 'library': return current === 'library' ? null : 'offer-library';
      default: return null; // project: conversation_scoped opens it
    }
  }
  private scoped(signal: AgentFollowSignal, active: boolean) {
    const scope = signal.event.data.scope || {}, title = str(signal.event.data.projectTitle, 120).trim();
    if (isObjectId(scope.projectId)) {
      this.lastProject = { projectId: scope.projectId, ...(isObjectId(scope.dpgfId) ? { dpgfId: scope.dpgfId } : {}) };
      if (active) this.want({ kind: 'project', projectId: scope.projectId, label: title ? `le projet « ${title} »` : 'le projet' }, signal.run!.id);
    } else this.projectRemoved(signal.scope.projectId || this.lastProject.projectId, signal.scope.dpgfId || this.lastProject.dpgfId);
  }
  // Project creation undone: its pages no longer exist; leave them for « Mes projets », whatever the follow state.
  private projectRemoved(projectId?: string, dpgfId?: string) {
    const path = pathOf(this.env.url()), ids = [projectId, dpgfId].filter(isObjectId);
    if (!ids.some(id => path.startsWith(`/architect/ce-project/${id}`) || path.startsWith(`/architect/dpgf/${id}`))) return;
    this.want({ kind: 'home', label: 'vos projets' }, '', true);
  }
  private toolStarted(signal: AgentFollowSignal, active: boolean) {
    const data = signal.event.data, target = normalizeTarget(data.target), dpgfId = signal.scope.dpgfId;
    if (!target.planId || !isObjectId(dpgfId)) return;
    const plan: AgentNavIntent = { kind: 'plan', dpgfId, planId: target.planId, label: 'le plan' };
    if (data.tool === 'measure_plan' && active) this.want(plan, signal.event.runId || signal.run!.id);
    else if (data.tool === 'inspect_plan' && !this.isShown(plan)) this.offer = plan; // reading only: « Voir le plan »
  }

  // ---- Intent execution -----------------------------------------------------------------

  private want(intent: AgentNavIntent, runId: string, forced = false) {
    if (intentKey(intent) !== intentKey(this.pending)) this.announced = { key: '', at: 0 };
    this.pending = intent; this.pendingRun = runId; this.forced = forced; this.message = '';
    if (intentKey(this.offer) === intentKey(intent)) this.offer = null;
  }
  private drop() { this.pending = null; this.pendingRun = ''; this.forced = false; this.announcing = false; this.hold = ''; this.holdText = ''; this.canSave = false; this.announced = { key: '', at: 0 }; }
  private valid(now: number) {
    const run = this.run;
    return this.forced || Boolean(run && run.id === this.pendingRun && (ACTIVE.includes(run.status) || now - run.endedAt < FOLLOW.graceMs));
  }
  private safe<T>(read: () => T, fallback: T): T { try { return read(); } catch { return fallback; } }
  // A registered page that shows the target (same lot, same plan…), or the same URL when no page can tell.
  isShown(intent: AgentNavIntent) {
    const pages = [...this.pages];
    if (pages.some(page => this.safe(() => page.shows?.(intent) === true, false))) return true;
    if (pages.some(page => typeof page.shows === 'function')) return false;
    return pathOf(this.env.url()) === intentRoute(intent).path;
  }
  private blockReason() {
    for (const page of this.pages) { const reason = this.safe(() => page.blockReason?.() || '', ''); if (reason) return str(reason, 200); }
    return '';
  }
  private whitelisted() { return this.pages.size > 0 || READ_ONLY_PAGES.some(pattern => pattern.test(pathOf(this.env.url()))); }

  private attempt() {
    this.clearTimer();
    const intent = this.pending, now = this.env.now();
    if (this.disposed || !intent || this.working || this.navigating) return this.update();
    if (this.isShown(intent)) { this.drop(); return this.update(); }
    if (!this.forced) {
      if (!this.owned) return this.wait('other-tab');
      if (this.paused) return this.wait('paused');
      if (!this.valid(now)) return this.wait('expired');
    }
    if (!this.env.visible()) return this.wait('hidden');
    if (this.env.modalOpen()) return this.wait('modal', FOLLOW.recheckMs);
    if (!this.forced) {
      if (!this.whitelisted()) return this.wait('elsewhere', FOLLOW.recheckMs);
      const reason = this.blockReason();
      if (reason) return this.wait('guard', FOLLOW.recheckMs, reason);
    }
    const target = pageType(intentRoute(intent).path), current = pageType(pathOf(this.env.url()));
    let due = this.lastActivity + FOLLOW.idleMs;
    if (!this.forced) {
      due = Math.max(due, this.lastFollowEnd + FOLLOW.dwellMs);
      if (target !== current && ['dpgf', 'plan'].includes(target) && ['dpgf', 'plan'].includes(current)) due = Math.max(due, this.pageSince + FOLLOW.pageTypeMs);
    }
    this.hold = ''; this.holdText = ''; this.canSave = false;
    if (due > now) { this.announcing = false; this.announced = { key: '', at: 0 }; this.schedule(due - now); return this.update(); }
    const key = intentKey(intent);
    if (this.announced.key !== key) this.announced = { key, at: now };
    const left = this.announced.at + FOLLOW.announceMs - now;
    if (left > 0) { this.announcing = true; this.schedule(left); return this.update(); }
    this.announcing = false;
    void this.navigate(intent);
  }
  private wait(hold: AgentFollowHold, recheck = 0, text = '') {
    this.hold = hold; this.holdText = text; this.announcing = false; this.announced = { key: '', at: 0 };
    this.canSave = hold === 'guard' && FLUSHABLE.includes(text) && [...this.pages].some(page => typeof page.flush === 'function');
    if (recheck) this.schedule(recheck);
    this.update();
  }
  private async navigate(intent: AgentNavIntent): Promise<boolean> {
    const { commands, queryParams } = intentRoute(intent);
    this.navigating = true; this.lastFollowStart = this.env.now(); this.update();
    let done = false;
    // false = cancelled (guard, newer navigation): nothing to say; a rejection is a loading error.
    try { done = await this.env.navigate(commands, { info: AGENT_FOLLOW_INFO, ...(queryParams ? { queryParams } : {}) }); } catch { this.message = 'La page n’a pas pu être ouverte.'; }
    this.navigating = false; this.lastFollowEnd = this.env.now();
    if (intentKey(this.pending) === intentKey(intent)) this.drop();
    if (intentKey(this.offer) === intentKey(intent)) this.offer = null;
    this.attempt();
    return done;
  }

  // ---- Bubble buttons -------------------------------------------------------------------

  // « Rester ici », switch off.
  pause() { this.paused = true; this.attempt(); }
  // « Reprendre le suivi », switch on, « Suivre ici » (with the panel state adopting the run).
  resume() { this.paused = false; this.owned = true; this.message = ''; this.attempt(); }
  // « Aller voir » / « Enregistrer et y aller » / « Voir le plan »: explicit click, no announcement and no delay.
  async open(intent: AgentNavIntent | null = this.pending): Promise<boolean> {
    if (!intent || this.working || this.navigating || this.disposed) return false;
    this.working = true; this.message = ''; this.clearTimer(); this.update();
    try {
      if (this.isShown(intent)) { if (intent === this.pending) this.drop(); if (intent === this.offer) this.offer = null; return true; }
      if (this.blockReason()) {
        const saved = await this.flushPages(), still = this.blockReason();
        if (!saved) { this.message = 'Vos modifications n’ont pas pu être enregistrées. La page reste ouverte.'; return false; }
        if (still) { this.message = `${still} : la page ne peut pas être quittée pour l’instant.`; return false; }
      }
      if (this.env.modalOpen()) { this.message = 'Fermez la fenêtre ouverte pour suivre l’IA.'; return false; }
      this.working = false;
      return await this.navigate(intent);
    } finally { this.working = false; this.attempt(); }
  }
  dismissOffer() { this.offer = null; this.update(); }

  // ---- Router and document --------------------------------------------------------------

  navigationStart(navigation: AgentNavigationStart) {
    if (this.disposed || navigation.info === AGENT_FOLLOW_INFO) return;  // ours, or a guard redirect of ours (info is carried)
    if (navigation.popstate) return this.manual();                     // back / forward: always the user
    if (navigation.info === AGENT_PANEL_INFO) return;                   // « Ouvrir la DPGF » of the panel
    if (pathOf(navigation.url) === pathOf(this.env.url())) return;      // query-only updates (?lotId strip, archi-db setUrl…)
    if (this.navigating && this.lastInput < this.lastFollowStart) return; // redirect of the follow navigation (no click since)
    const now = this.env.now();
    if (this.lastFollowStart > this.lastInput && now - this.lastFollowEnd < FOLLOW.chainMs) return; // page redirect (load error…)
    this.manual();
  }
  navigationEnd(succeeded: boolean) { if (succeeded) this.pageSince = this.env.now(); this.soon(); }
  // input: pointerdown / keydown (can start a navigation); other: wheel, touch scrolling.
  userActivity(input: boolean) {
    const now = this.env.now();
    this.lastActivity = now; if (input) this.lastInput = now;
    if (this.announcing) this.attempt();
  }
  visibilityChanged() { this.attempt(); }

  private manual() { if (!this.paused) { this.paused = true; this.attempt(); } }
  private soon() { if (this.pending && !this.navigating) { this.clearTimer(); this.schedule(0); } }
  private schedule(ms: number) { this.clearTimer(); this.timer = this.env.timers.setTimeout(() => { this.timer = null; this.attempt(); }, Math.max(0, ms)); }
  private clearTimer() { if (this.timer !== null) this.env.timers.clearTimeout(this.timer); this.timer = null; }
  private addHighlight(dpgfId: string, lotIds: string[] | null, postIds: string[]) {
    const now = this.env.now();
    for (const [key, entry] of this.highlights) if (now - entry.at > FOLLOW.highlightMs) this.highlights.delete(key);
    for (const lotId of lotIds || ['*']) {
      if (lotId !== '*' && !isLotId(lotId)) continue;
      const key = `${dpgfId}/${lotId}`, ids = [...new Set([...(this.highlights.get(key)?.ids || []), ...postIds])].slice(-FOLLOW.highlightIds);
      this.highlights.set(key, { ids, at: now });
    }
  }
  // The bubble is only refreshed when what it shows changed (the engine re-checks every second while blocked).
  private update() {
    const view = JSON.stringify([this.paused, this.owned, intentKey(this.pending), this.pending?.label, this.hold, this.holdText, this.canSave, this.announcing, intentKey(this.offer), this.message, this.working, this.navigating]);
    if (view === this.view) return;
    this.view = view;
    try { this.env.changed(); } catch { /* view errors are not engine errors */ }
  }
}
